/**
 * Code-based TanStack Router tree. We don't use the file-based plugin so this
 * file is the single source of truth for the route hierarchy. Each leaf
 * defers its UI to the corresponding `routes/...tsx` module.
 */
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router';
import { RootShell } from '@/routes/index';
import { ErrorPage } from '@/components/error-page';
import { SignupRoute } from '@/routes/_auth/signup';
import { LoginRoute } from '@/routes/_auth/login';
import { DashboardRoute } from '@/routes/_app/index';
import { ProjectsRoute } from '@/routes/_app/projects/index';
import { ProjectDetailRoute } from '@/routes/_app/projects/[id]';
import { AuditsRoute } from '@/routes/_app/audits/index';
import { AuditDetailRoute } from '@/routes/_app/audits/[id]';
import { KeywordsRoute } from '@/routes/_app/keywords/index';
import { KeywordRunDetailRoute } from '@/routes/_app/keywords/[id]';
import { BriefsRoute } from '@/routes/_app/briefs/index';
import { BriefDetailRoute } from '@/routes/_app/briefs/[id]';
import { OffPageBriefsRoute } from '@/routes/_app/off-page-briefs/index';
import { OffPageBriefDetailRoute } from '@/routes/_app/off-page-briefs/[id]';
import { ReportsRoute } from '@/routes/_app/reports/index';
import { ReportDetailRoute } from '@/routes/_app/reports/[id]';
import { AccountRoute } from '@/routes/_app/settings/account';
import { MembersRoute } from '@/routes/_app/settings/members';
import { BillingRoute } from '@/routes/_app/settings/billing';
import { IntegrationsRoute } from '@/routes/_app/settings/integrations';
import { CompetitorsRoute } from '@/routes/_app/competitors/index';
import { CampaignsRoute } from '@/routes/_app/campaigns/index';
import { CampaignDetailRoute } from '@/routes/_app/campaigns/[id]';
import { AutoFireDetailRoute } from '@/routes/_app/campaigns/auto/fires/[fireId]';
import { ForecastsRoute } from '@/routes/_app/forecasts/index';
import { ForecastDetailRoute } from '@/routes/_app/forecasts/[id]';
import { CompetitorRunDetailRoute } from '@/routes/_app/competitors/[id]';
import { CompetitorAnalysesRoute } from '@/routes/_app/competitor-analyses/index';
import { CompetitorAnalysisDetailRoute } from '@/routes/_app/competitor-analyses/[id]';
import { CalendarsRoute } from '@/routes/_app/calendars/index';
import { CalendarDetailRoute } from '@/routes/_app/calendars/[id]';
import { TrackingRoute } from '@/routes/_app/tracking/index';
import { ProtectedRoute } from '@/components/protected-route';
import { AppShell } from '@/components/app-shell';
import { useAuthStore } from '@/lib/auth';
import { sanitizeNextPath } from '@/lib/nav-registry';

const optionalString = (v: unknown): string | undefined =>
  typeof v === 'string' && v.length > 0 ? v : undefined;

const oneOf =
  <T extends string>(values: readonly T[]) =>
  (v: unknown): T | undefined =>
    typeof v === 'string' && (values as readonly string[]).includes(v)
      ? (v as T)
      : undefined;

/** Shared `?project=<id>` filter declaration for list pages. */
const projectFilterSearch = (
  search: Record<string, unknown>,
): { project?: string } => {
  const project = optionalString(search.project);
  return project ? { project } : {};
};

/**
 * The router-level not-found + error fallbacks render the same standalone
 * `<ErrorPage>` used by the global ErrorBoundary, so users get a consistent
 * branded screen no matter what went wrong.
 */
function RootNotFound(): JSX.Element {
  return <NotFoundForCurrentPath />;
}

function NotFoundForCurrentPath(): JSX.Element {
  // useRouterState here gives us the URL the user actually requested so the
  // error page can echo it back ("we couldn't find /foo/bar").
  const path = useRouterState({ select: (s) => s.location.pathname });
  return <ErrorPage variant="not-found" requestedPath={path} />;
}

function RootError({ error, reset }: { error: Error; reset: () => void }): JSX.Element {
  return <ErrorPage variant="error" error={error} onRetry={reset} />;
}

const rootRoute = createRootRoute({
  component: RootShell,
  notFoundComponent: RootNotFound,
  errorComponent: RootError,
});

// Auth-less layout — direct render, no shell. Already-authed visitors are
// bounced to the dashboard so /login isn't a reachable dead end for them.
const authedAwayGuard = (): void => {
  if (useAuthStore.getState().accessToken) {
    throw redirect({ to: '/' });
  }
};

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  validateSearch: (search: Record<string, unknown>): { next?: string } => {
    const next = sanitizeNextPath(search.next);
    return next ? { next } : {};
  },
  beforeLoad: authedAwayGuard,
  component: SignupRoute,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): { next?: string } => {
    const next = sanitizeNextPath(search.next);
    return next ? { next } : {};
  },
  beforeLoad: authedAwayGuard,
  component: LoginRoute,
});

// Authenticated layout. The beforeLoad guard redirects to /login BEFORE any
// child renders, carrying the attempted URL as `?next=` so login can return
// the user to it. ProtectedRoute stays as a render-time belt-and-suspenders
// for the token-cleared-while-mounted case.
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ location }) => {
    if (!useAuthStore.getState().accessToken) {
      throw redirect({
        to: '/login',
        search: { next: sanitizeNextPath(location.href) },
      });
    }
  },
  component: () => (
    <ProtectedRoute>
      <AppShell>
        <Outlet />
      </AppShell>
    </ProtectedRoute>
  ),
});

const dashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  component: DashboardRoute,
});

const projectsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/projects',
  component: ProjectsRoute,
});

const projectDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/projects/$projectId',
  // `google` is the OAuth return-leg marker (?google=connected|denied); the
  // page consumes it once (opens the Integrations tab, toasts) then strips it.
  validateSearch: (
    search: Record<string, unknown>,
  ): { google?: 'connected' | 'denied' } => {
    const google = oneOf(['connected', 'denied'] as const)(search.google);
    return google ? { google } : {};
  },
  component: ProjectDetailRoute,
});

const auditsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/audits',
  component: AuditsRoute,
});

const auditDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/audits/$auditId',
  // Tab + drawer state live in the URL so audit views are shareable and the
  // Back button can close a drawer / undo a tab change. Unknown tabs
  // normalize to `overview` instead of falling through to the wrong panel.
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    tab?: 'overview' | 'issues' | 'pages';
    issue?: string;
    page?: string;
    severity?: string;
  } => ({
    ...(() => {
      const tab = oneOf(['overview', 'issues', 'pages'] as const)(search.tab);
      return tab ? { tab } : {};
    })(),
    ...(() => {
      const issue = optionalString(search.issue);
      return issue ? { issue } : {};
    })(),
    ...(() => {
      const page = optionalString(search.page);
      return page ? { page } : {};
    })(),
    ...(() => {
      const severity = optionalString(search.severity);
      return severity ? { severity } : {};
    })(),
  }),
  component: AuditDetailRoute,
});

const keywordsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/keywords',
  component: KeywordsRoute,
});

const keywordRunDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/keywords/$keywordRunId',
  component: KeywordRunDetailRoute,
});

const briefsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/briefs',
  component: BriefsRoute,
});

const briefDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/briefs/$briefId',
  // Deep-linkable workspace tab (?tab=gaps etc.); the page validates the
  // value against its tab list and defaults to the writing plan.
  validateSearch: (search: Record<string, unknown>): { tab?: string } => {
    const tab = optionalString(search.tab);
    return tab ? { tab } : {};
  },
  component: BriefDetailRoute,
});

const offPageBriefsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/off-page-briefs',
  validateSearch: projectFilterSearch,
  component: OffPageBriefsRoute,
});

const offPageBriefDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/off-page-briefs/$offPageBriefId',
  component: OffPageBriefDetailRoute,
});

const reportsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports',
  component: ReportsRoute,
});

const reportDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reports/$reportId',
  component: ReportDetailRoute,
});

// Bare `/settings` → land on Account (the user's own page). Use beforeLoad
// throw-redirect so there's no render flash.
const settingsIndexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings',
  beforeLoad: () => {
    throw redirect({ to: '/settings/account' });
  },
  component: () => null,
});

const accountRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/account',
  component: AccountRoute,
});

const membersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/members',
  component: MembersRoute,
});

const billingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/billing',
  // Stripe checkout return leg (?status=success|cancel) — consumed once by
  // the page (toast + tenant refetch) and then stripped.
  validateSearch: (
    search: Record<string, unknown>,
  ): { status?: 'success' | 'cancel' } => {
    const status = oneOf(['success', 'cancel'] as const)(search.status);
    return status ? { status } : {};
  },
  component: BillingRoute,
});

const integrationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings/integrations',
  // Google OAuth deny leg lands here (?google=denied) — page toasts + strips.
  validateSearch: (
    search: Record<string, unknown>,
  ): { google?: 'connected' | 'denied' } => {
    const google = oneOf(['connected', 'denied'] as const)(search.google);
    return google ? { google } : {};
  },
  component: IntegrationsRoute,
});

const competitorsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/competitors',
  // Two surfaces share this route; `mode` makes them addressable/shareable.
  validateSearch: (
    search: Record<string, unknown>,
  ): { mode?: 'tracking' | 'research'; project?: string } => ({
    ...(() => {
      const mode = oneOf(['tracking', 'research'] as const)(search.mode);
      return mode ? { mode } : {};
    })(),
    ...projectFilterSearch(search),
  }),
  component: CompetitorsRoute,
});
const competitorRunDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/competitors/$runId',
  component: CompetitorRunDetailRoute,
});
const competitorAnalysesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/competitor-analyses',
  validateSearch: projectFilterSearch,
  component: CompetitorAnalysesRoute,
});
const competitorAnalysisDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/competitor-analyses/$analysisId',
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: 'strategy' | 'research' } => {
    const tab = oneOf(['strategy', 'research'] as const)(search.tab);
    return tab ? { tab } : {};
  },
  component: CompetitorAnalysisDetailRoute,
});
const trackingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tracking',
  component: TrackingRoute,
  validateSearch: projectFilterSearch,
});
const calendarsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/calendars',
  validateSearch: projectFilterSearch,
  component: CalendarsRoute,
});
const calendarDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/calendars/$calendarId',
  component: CalendarDetailRoute,
});
const campaignsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/campaigns',
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: 'auto' | 'manual'; project?: string } => ({
    ...(() => {
      const tab = oneOf(['auto', 'manual'] as const)(search.tab);
      return tab ? { tab } : {};
    })(),
    ...projectFilterSearch(search),
  }),
  component: CampaignsRoute,
});
const campaignDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/campaigns/$campaignId',
  component: CampaignDetailRoute,
});
const autoFireDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/campaigns/auto/fires/$fireId',
  component: AutoFireDetailRoute,
});
const forecastsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/forecasts',
  validateSearch: projectFilterSearch,
  component: ForecastsRoute,
});
const forecastDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/forecasts/$forecastId',
  component: ForecastDetailRoute,
});

const routeTree = rootRoute.addChildren([
  signupRoute,
  loginRoute,
  appLayoutRoute.addChildren([
    dashboardRoute,
    projectsRoute,
    projectDetailRoute,
    auditsRoute,
    auditDetailRoute,
    keywordsRoute,
    keywordRunDetailRoute,
    briefsRoute,
    briefDetailRoute,
    offPageBriefsRoute,
    offPageBriefDetailRoute,
    reportsRoute,
    reportDetailRoute,
    settingsIndexRoute,
    accountRoute,
    membersRoute,
    billingRoute,
    integrationsRoute,
    competitorsRoute,
    competitorRunDetailRoute,
    competitorAnalysesRoute,
    competitorAnalysisDetailRoute,
    trackingRoute,
    calendarsRoute,
    calendarDetailRoute,
    campaignsRoute,
    campaignDetailRoute,
    autoFireDetailRoute,
    forecastsRoute,
    forecastDetailRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Scroll to top on push navigation, restore position on Back/Forward —
  // without this, long list pages hand their scroll offset to the next page.
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
